import Papa from "papaparse";
import { useCallback } from "react";
import type { ColumnDefinition, DataRow } from "../../schema/chart";

// xlsx (~419 KB minified) is loaded lazily — only when the user clicks
// "Export to Excel". CSV export uses papaparse and doesn't touch xlsx.

function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

export function useTableExport() {
    const exportCSV = useCallback(
        (columns: ColumnDefinition[], rows: DataRow[], filename: string) => {
            const fields = columns.map((c) => c.name);
            const csv = Papa.unparse({ fields, data: rows });
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            downloadBlob(blob, filename.endsWith(".csv") ? filename : `${filename}.csv`);
        },
        [],
    );

    // Public callback stays synchronous — async dynamic-import work runs in a
    // void IIFE so existing call sites (onClick handlers) don't have to start
    // awaiting, and a failed import/write surfaces through console.error
    // (captured by Sentry's console integration in host apps) rather than
    // becoming an unhandled rejection.
    const exportExcel = useCallback(
        (columns: ColumnDefinition[], rows: DataRow[], filename: string) => {
            void (async () => {
                try {
                    const XLSX = await import("xlsx");
                    const fields = columns.map((c) => c.name);
                    const data = rows.map((row) => {
                        const obj: Record<string, string> = {};
                        for (const f of fields) {
                            obj[f] = row[f] ?? "";
                        }
                        return obj;
                    });
                    const ws = XLSX.utils.json_to_sheet(data, { header: fields });
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, "Data");
                    const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
                    const blob = new Blob([buf], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    });
                    downloadBlob(blob, filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`);
                } catch (err) {
                    console.error("Excel export failed", err);
                }
            })();
        },
        [],
    );

    return { exportCSV, exportExcel };
}
